import { computed, onMounted, ref } from 'vue';
import { REST_URL } from '@/lib/env';

interface ServiceHealthBackend {
  firebaseProject: string;
  firestoreDatabase: string;
  stripeMode: string;
  pupUrl: string;
}

interface ServiceHealth {
  service?: string;
  version?: string;
  commit?: string;
  environment?: string;
  backend?: ServiceHealthBackend;
}

/** LOCAL and TEST hosts get the badge; beta/prod never do. */
export function hostBadgeLabel(hostname: string): 'LOCAL' | 'TEST' | null {
  if (hostname === 'localhost' || hostname.endsWith('.local.dabblewriter.com')) return 'LOCAL';
  if (hostname.endsWith('.test.dabblewriter.com')) return 'TEST';
  return null;
}

/** Normalize rest's environment vocabulary for display. */
export function displayEnv(environment: string): string {
  if (environment === 'staging') return 'test';
  if (environment === 'production' || environment === 'prod') return 'PROD';
  return environment;
}

async function fetchRestHealth(timeoutMs = 2000): Promise<ServiceHealth | null> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const url = `${REST_URL.replace(/\/$/, '')}/health`;
    const response = await fetch(url, { cache: 'no-store', signal: controller.signal });
    if (!response.ok) return null;
    return (await response.json()) as ServiceHealth;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
}

/**
 * Dev/QA environment badge: which host flavor this is (LOCAL/TEST) and — from
 * rest's /health, the source of truth a URL can't provide — which backend the
 * app is really talking to (Firebase project, Firestore database, Stripe
 * mode), including the silent Caddy local → test fallback.
 */
export function useEnvBadge() {
  const badgeLabel = typeof window === 'undefined' ? null : hostBadgeLabel(window.location.hostname);
  const health = ref<ServiceHealth | null>(null);

  const restFallback = computed(() => {
    const environment = health.value?.environment;
    return badgeLabel === 'LOCAL' && Boolean(environment && environment !== 'local');
  });

  const badgeText = computed(() => {
    if (!badgeLabel) return null;
    const parts: string[] = [badgeLabel];
    if (restFallback.value) parts.push(`⚠️ rest → ${displayEnv(health.value!.environment!)}`);
    return parts.join(' · ');
  });

  const badgeTitle = computed(() => {
    const current = health.value;
    if (!current) return 'dabble-rest — unreachable';
    const lines: string[] = [];
    const name = current.service ?? 'dabble-rest';
    const version = current.version ? `@${current.version}` : '';
    const environment = current.environment ? ` (${displayEnv(current.environment)})` : '';
    lines.push(`${name}${version}${environment}`);
    const backend = current.backend;
    if (backend) {
      lines.push(`💾 DB: ${backend.firebaseProject} ${backend.firestoreDatabase}`);
      lines.push(`💳 Stripe: ${backend.stripeMode === 'live' ? '⚠️ LIVE' : backend.stripeMode}`);
    }
    return lines.join('\n');
  });

  onMounted(async () => {
    if (!badgeLabel) return;
    health.value = await fetchRestHealth();
  });

  return { badgeText, badgeTitle, restFallback };
}
